import useRemoveShopLogo from "@babackapp/private-api-client/client/use-remove-shop-logo";
import FormEditableAvatar from "@babackapp/react-hook-forms-components/form-editable-avatar";
import FormInput from "@babackapp/react-hook-forms-components/form-input";
import FormInputRow from "@babackapp/react-hook-forms-components/form-input-row";
import { SettingsFormSectionHeader } from "@babackapp/react-hook-forms-components/form-page";
import FormSwitch from "@babackapp/react-hook-forms-components/form-switch";
import useTranslation from "@babackapp/translations/use-translation";
import { Card, CardContent } from "@babackapp/ui/components/card";
import { FormField } from "@babackapp/ui/components/form";
import { useCallback } from "react";
import { useFormContext } from "react-hook-form";
import useUploadShopLogo from "@/api/shop-logo/use-upload-shop-logo";
import useCurrentShopLogoUrl from "@/api/shops/use-current-shop-logo-url";
import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense";
import { useViewerHasAtLeastAdminRights } from "@/lib/rights/use-viewer-has-right";
import type { ShopSettingsFormValues } from "./types";
import { OverrideRulesManager } from "@/components/override-rules";

export default function ShopSettingsInformationForm() {
  const t = useTranslation();
  const { data: shop } = useCurrentShopSuspense();
  const { control, resetField } = useFormContext<ShopSettingsFormValues>();

  const { mutateAsync: uploadShopLogo, isPending: uploadPending } =
    useUploadShopLogo();

  const defaultImage = useCurrentShopLogoUrl();
  const removeShopLogo = useRemoveShopLogo({ shopId: shop.id });

  console.log({ defaultImage });

  /**
   * If the logo has been updated and the field is dirty,
   * simply reset the field,
   * otherwise, remove the logo from the shop.
   */
  const _onRemoveShopLogo = useCallback(
    (isDirty: boolean) => {
      if (isDirty) return resetField("logoId");
      return removeShopLogo.mutate();
    },
    [removeShopLogo, resetField],
  );

  const isAdmin = useViewerHasAtLeastAdminRights();
  const disabledBecauseNotAdmin = isAdmin
    ? false
    : t("settings.shop.errors.notAdmin");

  /**
   * TODO: Placeholder skeleton
   */
  if (!shop) return null;

  return (
    <>
      <SettingsFormSectionHeader
        title={t("settings.shop.sections.basic.title")}
        id="information"
      />
      <Card>
        <CardContent className="py-8 flex flex-col gap-8">
          <FormInputRow>
            <FormField
              control={control}
              name="name"
              disabled={!isAdmin}
              render={({ field }) => (
                <FormInput
                  label={t("misc.name")}
                  placeholder={t("misc.name")}
                  {...field}
                  disabled={disabledBecauseNotAdmin}
                />
              )}
            />

            <FormField
              control={control}
              name="logoId"
              disabled={!isAdmin}
              render={({ field: { value }, fieldState: { isDirty } }) => (
                <FormEditableAvatar
                  label={t("settings.shop.logo")}
                  description={t("settings.shop.logoDescription")}
                  fallback={shop.name}
                  initialValue={value?.url ?? (defaultImage as any)?.data ?? ""}
                  disabled={disabledBecauseNotAdmin}
                  onRemove={() => _onRemoveShopLogo(isDirty)}
                  isLoading={removeShopLogo.isPending || uploadPending}
                  onUpload={uploadShopLogo}
                />
              )}
            />
          </FormInputRow>

          <FormField
            control={control}
            name="vatNumber"
            disabled={!isAdmin}
            render={({ field }) => (
              <FormInput
                label={t("settings.shop.vat")}
                description={t("settings.shop.vatDescription")}
                {...field}
                placeholder={t("settings.shop.vat")}
                disabled={disabledBecauseNotAdmin}
              />
            )}
          />
          <div>
            <FormField
              control={control}
              name="timeDelay"
              disabled={!isAdmin}
              render={({ field }) => (
                <FormInput
                  label={t("settings.shop.timeDelay.title")}
                  type="number"
                  description={t("settings.shop.timeDelay.description")}
                  placeholder={t("settings.shop.timeDelay.title")}
                  disabled={disabledBecauseNotAdmin}
                  value={field.value}
                  onChange={(e) => field.onChange(e.target.valueAsNumber)}
                />
              )}
            />
            <OverrideRulesManager
              actionType={"OVERRIDE_RETURN_DELAY"}
              excludeFields={[
                "returnItemCount",
                "returnItemResolutions",
                "returnReasonIds",
              ]}
            />
          </div>
          <FormInputRow>
            <FormField
              name="giftCode.activated"
              render={({ field }) => (
                <FormSwitch
                  label={t("settings.shop.giftCode.title")}
                  description={t("settings.shop.giftCode.description")}
                  {...field}
                />
              )}
            />
          </FormInputRow>
        </CardContent>
      </Card>
    </>
  );
}
